import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  ShieldExclamationIcon,
  XCircleIcon,
  RefreshIcon,
  TrashIcon,
  ClockIcon,
  ExclamationIcon
} from '@heroicons/react/solid';
import moment from 'moment';

const SecurityMonitoring = () => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [suspiciousIPs, setSuspiciousIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(t('common.accessDenied') || 'Access denied');
      navigate('/dashboard');
    }
  }, [user, navigate, t]);

  // Fetch security data
  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [blockedResponse, suspiciousResponse] = await Promise.all([
        api.get('/api/security/blocked'),
        api.get('/api/security/suspicious')
      ]);

      setBlockedIPs(blockedResponse.data.blocked || []);
      setSuspiciousIPs(suspiciousResponse.data.suspicious || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching security data:', err);
      setError(err.response?.data?.error || 'Failed to fetch security data');
      toast.error('Failed to fetch security data');
    } finally {
      setLoading(false);
    }
  };

  // Unblock an IP
  const handleUnblockIP = async (ip) => {
    if (!window.confirm(`Are you sure you want to unblock IP ${ip}?`)) {
      return;
    }

    try {
      await api.delete(`/api/security/blocked/${ip}`);
      toast.success(`IP ${ip} has been unblocked`);
      fetchSecurityData(); // Refresh the list
    } catch (err) {
      console.error('Error unblocking IP:', err);
      toast.error(err.response?.data?.error || 'Failed to unblock IP');
    }
  };

  // Set up automatic refresh
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSecurityData();

      let interval;
      if (isRefreshing) {
        interval = setInterval(() => {
          fetchSecurityData();
        }, refreshInterval * 1000);
      }

      return () => clearInterval(interval);
    }
  }, [refreshInterval, isRefreshing, user]);

  // Format time remaining for blocked IPs
  const formatTimeRemaining = (blockTime) => {
    const now = Date.now();
    const blockDuration = 24 * 60 * 60 * 1000; // 24 hours
    const elapsed = now - blockTime;
    const remaining = blockDuration - elapsed;

    if (remaining <= 0) {
      return 'Expired (will be removed on next refresh)';
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m remaining`;
  };

  // Format last seen time
  const formatLastSeen = (timestamp) => {
    return moment(timestamp).fromNow();
  };

  if (user?.role !== 'admin') {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {t('common.accessDenied') || 'Access denied. Admin access required.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('securityMonitoring.title') || 'Security Monitoring'}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('securityMonitoring.description') || 'Monitor blocked IPs and suspicious activity'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2 flex items-center">
          <div className="flex items-center">
            <label htmlFor="refresh" className="mr-2 text-sm">
              {t('securityMonitoring.refreshEvery') || 'Refresh every'}
            </label>
            <select
              id="refresh"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="border rounded p-1 text-sm"
              disabled={!isRefreshing}
            >
              <option value="10">10s</option>
              <option value="30">30s</option>
              <option value="60">1m</option>
              <option value="300">5m</option>
            </select>
          </div>

          <button
            onClick={() => setIsRefreshing(!isRefreshing)}
            className={`px-3 py-1 rounded text-white text-sm ${
              isRefreshing ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
            }`}
          >
            {isRefreshing
              ? t('securityMonitoring.stop') || 'Stop'
              : t('securityMonitoring.start') || 'Start'}
          </button>

          <button
            onClick={fetchSecurityData}
            disabled={loading}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 rounded text-white text-sm disabled:opacity-50 flex items-center"
          >
            <RefreshIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('securityMonitoring.refreshNow') || 'Refresh'}
          </button>
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-6">
        {t('securityMonitoring.lastRefreshed') || 'Last refreshed'}:{' '}
        {lastRefresh.toLocaleTimeString()}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('securityMonitoring.blockedIPs') || 'Blocked IPs'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">{blockedIPs.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('securityMonitoring.suspiciousIPs') || 'Suspicious IPs'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">{suspiciousIPs.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShieldExclamationIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('securityMonitoring.totalMonitored') || 'Total Monitored'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {blockedIPs.length + suspiciousIPs.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Blocked IPs Section */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {t('securityMonitoring.blockedIPs') || 'Blocked IP Addresses'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('securityMonitoring.blockedDescription') ||
              'IPs that have been automatically blocked after multiple suspicious requests. Blocks expire after 24 hours.'}
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-sm text-gray-500">
                {t('common.loading') || 'Loading...'}
              </p>
            </div>
          ) : blockedIPs.length === 0 ? (
            <div className="text-center py-8">
              <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {t('securityMonitoring.noBlockedIPs') || 'No IPs are currently blocked'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.ipAddress') || 'IP Address'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.status') || 'Status'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {blockedIPs.map((ip) => (
                    <tr key={ip}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{ip}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          {t('securityMonitoring.blocked') || 'Blocked'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleUnblockIP(ip)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          {t('securityMonitoring.unblock') || 'Unblock'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Suspicious IPs Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {t('securityMonitoring.suspiciousIPs') || 'Suspicious IP Addresses'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('securityMonitoring.suspiciousDescription') ||
              'IPs showing suspicious activity. They will be automatically blocked after 5 suspicious requests within 1 hour.'}
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-sm text-gray-500">
                {t('common.loading') || 'Loading...'}
              </p>
            </div>
          ) : suspiciousIPs.length === 0 ? (
            <div className="text-center py-8">
              <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {t('securityMonitoring.noSuspiciousIPs') || 'No suspicious IPs detected'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.ipAddress') || 'IP Address'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.suspiciousCount') || 'Suspicious Requests'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.lastSeen') || 'Last Seen'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.paths') || 'Paths Attempted'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('securityMonitoring.status') || 'Status'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {suspiciousIPs.map((item) => (
                    <tr key={item.ip}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.ip}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {item.count}/5
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="bg-yellow-600 h-2 rounded-full"
                              style={{ width: `${(item.count / 5) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {formatLastSeen(item.lastSeen)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          <div className="max-w-xs truncate" title={item.paths.join(', ')}>
                            {item.paths.slice(-3).join(', ')}
                            {item.paths.length > 3 && '...'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {t('securityMonitoring.monitoring') || 'Monitoring'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitoring;

